import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import type { SourceVideos, VideoDossier, VideosDuDossier } from './source.js';

/**
 * Onglet « Vidéos » du dossier (PARITE-5) : disposition de la démo (facturation-ged/VideosDossier) branchée sur l'API réelle.
 * Un lecteur dans l'onglet (youtube-nocookie.com), rattacher une adresse YouTube, retirer (confirmé). Partager au portail de la
 * famille : bouton explicite, puis une alerte qui LISTE les destinataires ; rien ne part avant « Confirmer le partage ».
 */
export interface PropsVueVideos {
  source: SourceVideos;
  peutEcrire: boolean;
  peutPartager: boolean;
}

const RAISON_ECRIRE = 'Votre rôle ne permet pas de modifier les vidéos du dossier.';
const RAISON_PARTAGER = 'Votre rôle ne permet pas de partager une vidéo hors de l’équipe.';
const SANS_DESTINATAIRE = 'Aucun membre de la famille n’a encore accès au portail : rien ne peut être partagé.';

const messageDe = (e: unknown): string => {
  if (e instanceof ErreurApi) {
    if (e.code === 'VERSION_CONFLICT') return 'La liste des destinataires a changé : relisez-la avant de partager.';
    if (e.code === 'VALIDATION_FAILED' && e.detail) return e.detail;
    return e.messageUtilisateur;
  }
  return e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';
};

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const titre: CSSProperties = { margin: 0, fontSize: 18, color: COULEURS.marine };
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13, margin: 0 };
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  width: '100%',
  boxSizing: 'border-box',
};
const actions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const badge = (fond: string, encre: string): CSSProperties => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: fond,
  color: encre,
  borderRadius: 999,
  padding: '2px 10px',
});

function Champ({ pour, libelle, children }: { pour: string; libelle: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <label htmlFor={pour} style={{ fontWeight: 600, fontSize: 14 }}>
        {libelle}
      </label>
      {children}
    </div>
  );
}

function Lecteur({ video }: { video: VideoDossier }) {
  return (
    <section aria-label="Lecteur vidéo" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <strong style={{ overflowWrap: 'anywhere' }}>{video.title}</strong>
      <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
        <iframe
          src={video.embedUrl}
          title={`Lecteur : ${video.title}`}
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </div>
    </section>
  );
}

export function VueVideos({ source, peutEcrire, peutPartager }: PropsVueVideos) {
  const [donnees, setDonnees] = useState<VideosDuDossier | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [lue, setLue] = useState<string | null>(null);
  const [titreSaisi, setTitreSaisi] = useState('');
  const [adresse, setAdresse] = useState('');
  const [aPartager, setAPartager] = useState<VideoDossier | null>(null);
  const [aRetirer, setARetirer] = useState<VideoDossier | null>(null);

  const charger = useCallback(async () => {
    try {
      setDonnees(await source.lister());
    } catch (e) {
      setErreur(messageDe(e));
    }
  }, [source]);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function agir(action: () => Promise<unknown>, apres?: () => void) {
    setEnCours(true);
    setErreur(null);
    try {
      await action();
      apres?.();
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setEnCours(false);
      await charger();
    }
  }

  const videos = donnees?.videos ?? [];
  const destinataires = donnees?.recipients ?? [];
  const courante = videos.find((v) => v.id === lue) ?? videos[0] ?? null;
  const raisonPartage = !peutPartager ? RAISON_PARTAGER : destinataires.length === 0 ? SANS_DESTINATAIRE : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <section aria-label="Vidéos du dossier" style={carte}>
        <h2 style={titre}>Vidéos du dossier</h2>
        <span style={{ ...badge('#EEF0F8', COULEURS.marine), alignSelf: 'flex-start' }}>visible : équipe MAAT</span>
        <p style={discret}>
          Plusieurs vidéos peuvent être rattachées à un dossier (présentation du cabinet, explication d’une étape). Collez l’adresse YouTube
          ; la vidéo reste réservée à l’équipe tant que vous ne la partagez pas avec la famille.
        </p>
        {erreur ? (
          <p role="alert" style={{ margin: 0, color: COULEURS.rouge, fontWeight: 600 }}>
            {erreur}
          </p>
        ) : null}
        {courante ? <Lecteur video={courante} /> : null}
        {donnees && videos.length === 0 ? <p style={discret}>Aucune vidéo rattachée à ce dossier.</p> : null}
        {videos.length > 0 ? (
          <ul aria-label="Vidéos rattachées" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {videos.map((v) => (
              <li
                key={v.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: '1px solid #EEEDF3',
                }}
              >
                <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <strong style={{ overflowWrap: 'anywhere' }}>{v.title}</strong>{' '}
                  {v.sharedWithFamilyAt ? (
                    <span style={badge('#E4F3EC', '#1F5E43')}>{`Partagée avec la famille : ${v.sharedWith.join(', ')}`}</span>
                  ) : (
                    <span style={badge('#EEF0F8', COULEURS.marine)}>Équipe MAAT seulement</span>
                  )}
                  <br />
                  <span style={{ ...discret, overflowWrap: 'anywhere' }}>{v.url}</span>
                </span>
                <span style={actions}>
                  <button type="button" style={styleBouton} aria-label={`Regarder : ${v.title}`} onClick={() => setLue(v.id)}>
                    Regarder
                  </button>
                  {v.sharedWithFamilyAt ? (
                    <button
                      type="button"
                      style={styleBouton}
                      aria-label={`Retirer le partage : ${v.title}`}
                      disabled={!peutPartager || enCours}
                      title={peutPartager ? undefined : RAISON_PARTAGER}
                      onClick={() => void agir(() => source.retirerPartage(v.id))}
                    >
                      Retirer le partage
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={styleBouton}
                      aria-label={`Partager avec la famille : ${v.title}`}
                      disabled={raisonPartage !== undefined || enCours}
                      title={raisonPartage}
                      onClick={() => setAPartager(v)}
                    >
                      Partager avec la famille
                    </button>
                  )}
                  <button
                    type="button"
                    style={styleBouton}
                    aria-label={`Retirer : ${v.title}`}
                    disabled={!peutEcrire || enCours}
                    title={peutEcrire ? undefined : RAISON_ECRIRE}
                    onClick={() => setARetirer(v)}
                  >
                    Retirer
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {donnees && destinataires.length === 0 && videos.length > 0 ? <p style={discret}>{SANS_DESTINATAIRE}</p> : null}

        {aPartager ? (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="video-partage-titre"
            style={{ ...carte, border: `2px solid ${COULEURS.boutonPrincipal}` }}
          >
            <h3 id="video-partage-titre" style={{ margin: 0, fontSize: 16, color: COULEURS.marine }}>
              {`Partager « ${aPartager.title} » avec la famille ?`}
            </h3>
            <p
              style={{ margin: 0 }}
            >{`La vidéo sera visible sur le portail de ces ${destinataires.length} personne${destinataires.length > 1 ? 's' : ''} :`}</p>
            <ul aria-label="Destinataires" style={{ margin: 0, paddingLeft: 20 }}>
              {destinataires.map((d) => (
                <li key={d.userId}>{d.displayName}</li>
              ))}
            </ul>
            <div style={actions}>
              <button
                type="button"
                style={styleBoutonPrincipal}
                disabled={enCours}
                onClick={() => {
                  const video = aPartager;
                  void agir(
                    () =>
                      source.partager(
                        video.id,
                        destinataires,
                        destinataires.map((d) => d.displayName),
                      ),
                    () => setAPartager(null),
                  ).then(() => setAPartager(null));
                }}
              >
                Confirmer le partage
              </button>
              <button type="button" style={styleBouton} disabled={enCours} onClick={() => setAPartager(null)}>
                Annuler
              </button>
            </div>
          </div>
        ) : null}

        {aRetirer ? (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="video-retrait-titre"
            style={{ ...carte, border: `2px solid ${COULEURS.rouge}` }}
          >
            <h3 id="video-retrait-titre" style={{ margin: 0, fontSize: 16, color: COULEURS.marine }}>
              Retirer la vidéo ?
            </h3>
            <p
              style={{ margin: 0 }}
            >{`« ${aRetirer.title} » ne sera plus rattachée à ce dossier${aRetirer.sharedWithFamilyAt ? ' ni visible sur le portail de la famille' : ''}. La vidéo elle-même reste sur YouTube.`}</p>
            <div style={actions}>
              <button
                type="button"
                style={{ ...styleBoutonPrincipal, background: COULEURS.rouge, borderColor: COULEURS.rouge }}
                disabled={enCours}
                onClick={() => {
                  const video = aRetirer;
                  void agir(
                    () => source.retirer(video.id),
                    () => setARetirer(null),
                  );
                }}
              >
                Retirer la vidéo
              </button>
              <button type="button" style={styleBouton} disabled={enCours} onClick={() => setARetirer(null)}>
                Annuler
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section aria-label="Rattacher une vidéo" style={carte}>
        <h2 style={titre}>Rattacher une vidéo</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <Champ pour="video-titre" libelle="Titre de la vidéo">
            <input
              id="video-titre"
              value={titreSaisi}
              maxLength={120}
              disabled={!peutEcrire || enCours}
              onChange={(e) => setTitreSaisi(e.target.value)}
              style={champ}
            />
          </Champ>
          <Champ pour="video-adresse" libelle="Adresse YouTube">
            <input
              id="video-adresse"
              value={adresse}
              inputMode="url"
              placeholder="https://www.youtube.com/watch?v=…"
              disabled={!peutEcrire || enCours}
              onChange={(e) => setAdresse(e.target.value)}
              style={champ}
            />
          </Champ>
        </div>
        {!peutEcrire ? <p style={discret}>{RAISON_ECRIRE}</p> : null}
        <div style={actions}>
          <button
            type="button"
            style={styleBoutonPrincipal}
            disabled={!peutEcrire || enCours || !titreSaisi.trim() || !adresse.trim()}
            title={
              !titreSaisi.trim() ? 'Donnez un titre à la vidéo.' : !adresse.trim() ? 'Collez l’adresse YouTube de la vidéo.' : undefined
            }
            onClick={() =>
              void agir(
                () => source.rattacher(titreSaisi.trim(), adresse.trim()),
                () => {
                  setTitreSaisi('');
                  setAdresse('');
                },
              )
            }
          >
            Rattacher la vidéo
          </button>
        </div>
      </section>
    </div>
  );
}
